import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";

export interface AnalyticsEventInput {
  event: string;
  page?: string | null;
  props?: Record<string, string | number | boolean | null> | null;
  sessionId?: string | null;
}

const ALLOWED_EVENTS = new Set([
  "page_view",
  "signup_completed",
  "free_session_cta_click",
  "free_session_registered",
  "free_session_calendar_google",
  "free_session_calendar_ics",
  "free_session_bridge_click",
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Public, fire-and-forget analytics sink for the free-sessions funnel. */
export const recordAnalyticsEvent = createServerFn({ method: "POST" })
  .inputValidator((input: AnalyticsEventInput) => input)
  .handler(async ({ data }) => {
    const event = (data.event || "").trim().slice(0, 80);
    if (!ALLOWED_EVENTS.has(event)) return { ok: false };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Cheap abuse guard so the sink cannot be flooded from one client.
    const ip = (getRequestHeader("x-forwarded-for") || "").split(",")[0]?.trim() || "unknown";
    const { data: allowed } = await supabaseAdmin.rpc("bump_rate_limit", {
      _key: `analytics:${ip}`,
      _limit: 120,
      _window_seconds: 60,
    });
    if (allowed === false) return { ok: false };

    const sessionId = data.sessionId && UUID_RE.test(data.sessionId) ? data.sessionId : null;

    await supabaseAdmin.from("analytics_events").insert({
      event_name: event,
      page: (data.page || "").slice(0, 200) || null,
      session_id: sessionId,
      props: JSON.parse(JSON.stringify(data.props ?? {})),
    });

    return { ok: true };
  });
